import React, { useEffect, useState } from "react";
import { db } from "../../api";
import { collection, onSnapshot, query, where, orderBy } from "../../api";
import { useAuth } from "../../context/AuthContext";
import InvoiceView from "./InvoiceView";

export default function UserReservations() {
  const { currentUser } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, "reservations"),
      where("userId", "==", currentUser.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const reservationList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReservations(reservationList);
    });

    return () => unsub();
  }, [currentUser]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'active': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'completed': return 'text-green-600 bg-green-100 border-green-200';
      case 'cancelled': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  const getFilteredReservations = () => {
    switch (activeTab) {
      case 'active':
        return reservations.filter(r => r.status === 'pending' || r.status === 'active');
      case 'completed':
        return reservations.filter(r => r.status === 'completed');
      case 'cancelled':
        return reservations.filter(r => r.status === 'cancelled');
      default:
        return reservations;
    }
  };

  const filteredReservations = getFilteredReservations();

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-4 border-b border-gray-300">
        <button
          onClick={() => setActiveTab('active')}
          className={`py-2 px-4 font-medium ${
            activeTab === 'active'
              ? 'text-burgundy border-b-2 border-burgundy'
              : 'text-navy hover:text-burgundy'
          }`}
        >
          Active Rentals
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`py-2 px-4 font-medium ${
            activeTab === 'completed'
              ? 'text-burgundy border-b-2 border-burgundy'
              : 'text-navy hover:text-burgundy'
          }`}
        >
          Completed
        </button>
        <button
          onClick={() => setActiveTab('cancelled')}
          className={`py-2 px-4 font-medium ${
            activeTab === 'cancelled'
              ? 'text-burgundy border-b-2 border-burgundy'
              : 'text-navy hover:text-burgundy'
          }`}
        >
          Cancelled
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`py-2 px-4 font-medium ${
            activeTab === 'all'
              ? 'text-burgundy border-b-2 border-burgundy'
              : 'text-navy hover:text-burgundy'
          }`}
        >
          All
        </button>
      </div>

      {/* Reservations List */}
      {filteredReservations.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-navy text-lg">No reservations found.</p>
          <p className="text-gray-600 mt-2">
            {activeTab === 'active' && "You don't have any active rentals."}
            {activeTab === 'completed' && "You haven't completed any rentals yet."}
            {activeTab === 'cancelled' && "You don't have any cancelled reservations."}
            {activeTab === 'all' && "You haven't made any reservations yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReservations.map((reservation) => (
            <div
              key={reservation.id}
              className="bg-white border border-gray-300 rounded shadow p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-burgundy mb-1">
                    {reservation.itemName || 'Unknown Item'}
                  </h3>
                  <p className="text-gray-600">{reservation.category}</p>
                </div>
                <span className={`px-3 py-1 rounded border ${getStatusColor(reservation.status || 'pending')}`}>
                  {(reservation.status || 'pending').toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Start Date</p>
                  <p className="font-medium">{formatDate(reservation.startDate)}</p>
                </div>
                <div>
                  <p className="text-gray-600">End Date</p>
                  <p className="font-medium">{formatDate(reservation.endDate)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Cost</p>
                  <p className="font-medium text-green-600">${reservation.totalCost || 0}</p>
                </div>
                {reservation.size && (
                  <div>
                    <p className="text-gray-600">Size</p>
                    <p className="font-medium">{reservation.size}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-600">Daily Rate</p>
                  <p className="font-medium">${reservation.dailyRate || 0}/day</p>
                </div>
                <div>
                  <p className="text-gray-600">Reserved</p>
                  <p className="font-medium">{formatDate(reservation.createdAt)}</p>
                </div>
              </div>

              {reservation.customerNotes && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-gray-600 text-sm mb-1">Your Notes:</p>
                  <p className="text-sm">{reservation.customerNotes}</p>
                </div>
              )}

              {/* Status-specific information */}
              {reservation.status === 'pending' && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="text-yellow-800 text-sm font-medium">
                      Your reservation is pending confirmation. You'll be notified once it's approved.
                    </p>
                  </div>
                </div>
              )}

              {reservation.status === 'active' && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-blue-800 text-sm font-medium">
                      Equipment picked up! Please return by {formatDate(reservation.endDate)}.
                    </p>
                    {reservation.pickedUpAt && (
                      <p className="text-blue-700 text-xs mt-1">
                        Picked up on: {formatDate(reservation.pickedUpAt)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {reservation.status === 'completed' && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="text-green-800 text-sm font-medium">
                      Rental completed successfully!
                    </p>
                    {reservation.returnedAt && (
                      <p className="text-green-700 text-xs mt-1">
                        Returned on: {formatDate(reservation.returnedAt)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Invoice Button */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setSelectedInvoice(reservation)}
                  className="bg-navy text-white py-2 px-4 rounded hover:bg-opacity-90 transition text-sm font-medium"
                >
                  View reservation summary
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invoice Modal */}
      {selectedInvoice && (
        <InvoiceView
          reservation={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </div>
  );
}